import React, { useCallback, useEffect, useState } from 'react';
import changeHistoryService from '../services/changeHistoryService';
import Pagination from '../components/Pagination';
import ChangeHistoryTimeline from '../components/ChangeHistoryTimeline';
import { SearchIcon, FilterIcon } from '../components/Icons';

const moduleOptions = [
  { value: '', label: 'All Modules' },
  { value: 'merchants', label: 'Merchants' },
  { value: 'merchant_credentials', label: 'Merchant Credentials' },
  { value: 'users', label: 'Users' },
  { value: 'tickets', label: 'Tickets' },
  { value: 'ticket_workflows', label: 'Ticket Workflows' },
  { value: 'audit_settings', label: 'Audit Settings' },
  { value: 'master_providers', label: 'L1 Providers' },
  { value: 'providers', label: 'Providers' },
  { value: 'sources', label: 'Provider Sources' },
  { value: 'payment_gateway_categories', label: 'Payment Gateway Categories' },
  { value: 'payment_gateway_sources', label: 'Payment Gateway Provider Sources' },
  { value: 'payment_gateway_providers', label: 'Payment Gateway Providers' },
  { value: 'payment_gateway_master_providers', label: 'L1 Payment Gateway Providers' },
  { value: 'currencies', label: 'Currencies' },
  { value: 'countries', label: 'Countries' },
  { value: 'categories', label: 'Categories' },
  { value: 'sub_categories', label: 'Sub Categories' }
];

const operationOptions = [
  { value: '', label: 'All Actions' },
  { value: 'create', label: 'Created' },
  { value: 'update', label: 'Updated' },
  { value: 'delete', label: 'Deleted' }
];

const ChangeHistory = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [filters, setFilters] = useState({
    module: '',
    operation: '',
    search: ''
  });

  const fetchHistory = useCallback(async (page = 1, limit = pagination.limit) => {
    try {
      setLoading(true);
      setError('');
      const res = await changeHistoryService.getHistory({
        page,
        limit,
        ...filters
      });

      if (res.success) {
        setHistory(res.data || []);
        setPagination(prev => ({
          ...prev,
          page: res.pagination.page,
          limit: res.pagination.limit,
          total: res.pagination.total,
          pages: Math.max(1, res.pagination.totalPages || 1)
        }));
      }
    } catch (err) {
      setError('An error occurred while fetching change history');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]);

  useEffect(() => {
    fetchHistory(1);
  }, [fetchHistory]);

  return (
    <div className="change-history-page">
      <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem' }}>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="search-container" style={{ flex: '1 1 260px', maxWidth: 'none' }}>
            <div className="search-icon-wrapper"><SearchIcon /></div>
            <input
              type="text"
              className="search-input"
              placeholder="Search actor, record, field, route..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            />
          </div>

          <select
            className="input"
            value={filters.module}
            onChange={(e) => setFilters(prev => ({ ...prev, module: e.target.value }))}
            style={{ flex: '0 1 190px', minWidth: '160px' }}
          >
            {moduleOptions.map(option => (
              <option key={option.value || 'all'} value={option.value}>{option.label}</option>
            ))}
          </select>

          <select
            className="input"
            value={filters.operation}
            onChange={(e) => setFilters(prev => ({ ...prev, operation: e.target.value }))}
            style={{ flex: '0 1 170px', minWidth: '150px' }}
          >
            {operationOptions.map(option => (
              <option key={option.value || 'all'} value={option.value}>{option.label}</option>
            ))}
          </select>

          <button className="btn btn-outline" onClick={() => fetchHistory(1)} style={{ minHeight: '42px' }}>
            <FilterIcon size={16} style={{ marginRight: '0.5rem' }} />
            Apply
          </button>
        </div>
      </div>

      {error ? (
        <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--error)' }}>{error}</div>
      ) : (
        <div className="card" style={{ padding: '1rem' }}>
          <ChangeHistoryTimeline
            entries={history}
            loading={loading && history.length === 0}
            emptyMessage="No change history found."
          />
          <Pagination
            pagination={pagination}
            onPageChange={(pg) => fetchHistory(pg)}
            onLimitChange={(limit) => {
              setPagination(prev => ({ ...prev, limit }));
              fetchHistory(1, limit);
            }}
            loading={loading}
            itemName="history entries"
          />
        </div>
      )}
    </div>
  );
};

export default ChangeHistory;
